/**
 * `/pack/:handleTail` — where litko.net's Cloudflare redirect sends a pack's
 * Shopify product page (`/products/ctok-<handleTail>`), so a shopper who clicks
 * the cart line lands in the designer, not on the pack's own buy box.
 *
 * The handle tail is the design token with `_` → `-` (`dvk-<uuid>`). Until the
 * change-quantity round trip exists (after P1b), the studio opens fresh on the
 * pack product; the token rides along as `?pack=dvk_…` for that later step and
 * is ignored today. A malformed tail opens the studio without it.
 *
 * ⚠ The `/pack/` path is a contract with a Cloudflare rule that no test can see
 * (docs/operations/storefront-access-plane.md §9): change the two together.
 */

/**
 * The product a pack link opens until the token can name its own CIT. The recovery
 * page's "Go to My tokens" opens it too (a bare `/` needs VITE_DEFAULT_CIT_KEY, which
 * a build may not set: the 10-22 live walk landed on "No product selected").
 */
export const PACK_CIT_KEY = import.meta.env.VITE_PACK_CIT_KEY ?? 'CTOK001';

const HANDLE_TAIL = /^dvk-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;

/** The design token a pack handle tail names, or null when it isn't one. */
export function tokenFromHandleTail(tail: string): string | null {
    const m = HANDLE_TAIL.exec(tail.toLowerCase());
    return m ? `dvk_${m[1]}` : null;
}

/** The design token a studio URL's `?pack=` carries, or null when absent or malformed. */
export function tokenFromQuery(search: string): string | null {
    const raw = new URLSearchParams(search).get('pack');
    return raw !== null && /^dvk_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(raw) ? raw : null;
}

/** Rewrites a `/pack/…` URL to the studio route in place; a no-op on any other path. */
export function rewritePackLink(): void {
    const m = /^\/pack\/([^/]+)\/?$/.exec(window.location.pathname);
    if (m === null) {
        return;
    }
    const token = tokenFromHandleTail(decodeURIComponent(m[1]));
    const query = token !== null ? `?pack=${encodeURIComponent(token)}` : '';
    window.history.replaceState(null, '', `/design/${encodeURIComponent(PACK_CIT_KEY)}${query}`);
}
